import {
  DataFilterDefinitionsDtoResponse,
  GenerateNavItemLabelLinesRequestDto,
  NavItemLabelDto,
  NavItemLabelLineDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';

import { formatPageFilterUrlValuesForDisplay } from '../components/filter/formatter';
import { PageFilterSelections } from '../store/hooks/useCombinedPageFilterSelections';
import { FilterConfigDto } from '../store/states/filterTypes';

import { findOptionLabel } from './comparisonDetailPageNav';
import { DetailPageNavigationIdentity } from './detailPageNavigationIdentity';
import { formatSourceTableColumnValue } from './formatSourceTableColumnValue';

const ROW_LABEL_SEPARATOR = ' - ';

export function buildGenerateNavItemLabelLinesRequest(
  identity: DetailPageNavigationIdentity,
  combinedSelections: PageFilterSelections
): GenerateNavItemLabelLinesRequestDto {
  const selection: GenerateNavItemLabelLinesRequestDto['selection'] = {};

  for (const key of identity.hiddenFilterKeys) {
    const value = combinedSelections[key];
    if (value !== undefined) {
      selection[key] = value;
    }
  }

  return {
    detailsDiscriminatorKey: identity.detailsDiscriminator,
    selection,
  };
}

export function hashHiddenNavItemLabelSelection(
  identity: DetailPageNavigationIdentity,
  combinedSelections: PageFilterSelections
): string {
  const request = buildGenerateNavItemLabelLinesRequest(
    identity,
    combinedSelections
  );
  const keys = Object.keys(request.selection).sort();
  return JSON.stringify(
    Object.fromEntries(keys.map((key) => [key, request.selection[key]]))
  );
}

export function fallbackNavItemLabelsWhenEmpty(
  pageData?: PageDto
): NavItemLabelDto[] {
  const title = pageData?.pageHeader?.title;
  if (title != null && title.length > 0) {
    return [{ kind: 'pageTitle', displayValue: title }];
  }
  return [];
}

export type MapNavItemLabelLinesContext = {
  pageData?: PageDto;
  detailsDiscriminatorKey: string;
  filterConfigByKey: Record<string, FilterConfigDto>;
  detailsDiscriminatorDisplayValue?: string;
  pageFilterDefinitions?: DataFilterDefinitionsDtoResponse['data'];
};

function formatRawPrimitiveValue(
  value: string | number | boolean | null
): string {
  if (value === null) {
    return '';
  }
  return String(value);
}

function formatPageFilterValuesIndividually(
  values: Array<string | number | boolean | null>,
  filterConfig: FilterConfigDto | undefined
): string[] {
  return values.map((value) =>
    value == null
      ? ''
      : formatPageFilterUrlValuesForDisplay([String(value)], filterConfig)
  );
}

export type RowGroupableNavItemLabel = Omit<NavItemLabelDto, 'displayValue'> & {
  displayValues: string[];
  groupByRow: boolean;
};

function toDisplayLabel(label: RowGroupableNavItemLabel): NavItemLabelDto {
  return {
    kind: label.kind,
    ...(label.key != null ? { key: label.key } : {}),
    displayValue: label.displayValues.join(', '),
  };
}

export function groupNavItemLabelsByRow(
  labels: RowGroupableNavItemLabel[]
): NavItemLabelDto[] {
  const rowLabels = labels.filter((label) => label.groupByRow);
  const shouldGroupRows =
    rowLabels.length > 1 &&
    rowLabels.some((label) => label.displayValues.length > 1);

  if (!shouldGroupRows) {
    return labels.map(toDisplayLabel);
  }

  const staticLabels = labels
    .filter((label) => !label.groupByRow)
    .map(toDisplayLabel);
  const rowCount = Math.max(
    ...rowLabels.map((label) => Math.max(label.displayValues.length, 1))
  );

  const groupedRows = Array.from({ length: rowCount }, (_, index) => {
    const displayValue = rowLabels
      .map((label) => label.displayValues[index] ?? '')
      .filter((value) => value.length > 0)
      .join(ROW_LABEL_SEPARATOR);
    const firstRowLabel = rowLabels[0];
    return {
      kind: firstRowLabel.kind,
      ...(firstRowLabel.key != null ? { key: firstRowLabel.key } : {}),
      displayValue,
    };
  });

  return [...staticLabels, ...groupedRows];
}

function resolveDiscriminatorDisplayValue(
  rawValue: string | number | boolean | null,
  context: MapNavItemLabelLinesContext
): string {
  if (context.detailsDiscriminatorDisplayValue != null) {
    return context.detailsDiscriminatorDisplayValue;
  }

  if (rawValue == null) {
    return '';
  }

  const rawString = String(rawValue);
  const definition =
    context.pageFilterDefinitions?.[context.detailsDiscriminatorKey];
  const optionLabel = findOptionLabel(definition, rawString);
  if (optionLabel != null) {
    return optionLabel;
  }

  return formatPageFilterUrlValuesForDisplay(
    [rawString],
    context.filterConfigByKey[context.detailsDiscriminatorKey]
  );
}

function normalizeLineValues(
  value: NavItemLabelLineDto['value']
): Array<string | number | boolean | null> {
  if (Array.isArray(value)) {
    return value.length > 0 ? value : [null];
  }
  return [value];
}

export function mapNavItemLabelLinesToDisplay(
  lines: NavItemLabelLineDto[],
  context: MapNavItemLabelLinesContext
): NavItemLabelDto[] {
  const columnConfigByKey = Object.fromEntries(
    (context.pageData?.detailPageNavItemSourceTableColumns ?? []).map(
      (column) => [column.key, column]
    )
  );

  const labels = lines.map<RowGroupableNavItemLabel>((line) => {
    const values = normalizeLineValues(line.value);

    if (line.kind === 'pageTitle') {
      return {
        kind: 'pageTitle',
        groupByRow: false,
        displayValues: [
          context.pageData?.pageHeader?.title ??
            formatRawPrimitiveValue(values[0] ?? null),
        ],
      };
    }

    if (line.kind === 'detailsDiscriminatorValue') {
      return {
        kind: 'detailsDiscriminatorValue',
        groupByRow: true,
        displayValues:
          values.length === 1
            ? [resolveDiscriminatorDisplayValue(values[0] ?? null, context)]
            : formatPageFilterValuesIndividually(
                values,
                context.filterConfigByKey[context.detailsDiscriminatorKey]
              ),
      };
    }

    const key = line.key ?? '';
    const columnConfig = columnConfigByKey[key];
    return {
      kind: 'columnKey',
      key,
      groupByRow: true,
      displayValues:
        columnConfig != null
          ? values.map((value) =>
              formatSourceTableColumnValue(value, columnConfig)
            )
          : values.map(formatRawPrimitiveValue),
    };
  });

  return groupNavItemLabelsByRow(labels);
}

export function navItemLabelsEqual(
  a: NavItemLabelDto[] | null | undefined,
  b: NavItemLabelDto[] | null | undefined
): boolean {
  const aArr = a ?? [];
  const bArr = b ?? [];
  if (aArr.length !== bArr.length) {
    return false;
  }
  return aArr.every(
    (item, index) =>
      item.kind === bArr[index]?.kind &&
      item.key === bArr[index]?.key &&
      item.displayValue === bArr[index]?.displayValue
  );
}
